import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AutoComplete } from 'primeng/autocomplete';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AutocompleteHit,
  AutocompleteResponse,
  BuiltinControlAutocompleteRequest,
  ControlIdentity,
  CplaceSingleReferenceControlChange,
  CplaceSingleReferenceControlParameters,
  ReferenceValue,
} from '../../../api/model/models';
import { TranslationService } from '../../../i18n';
import { CplaceControlService, CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';
import { AutoCompleteEventObject } from './types';

/**
 * Control for editing single reference data types in cplace.
 */
@UntilDestroy()
@Component({
  selector: 'cf-frontend-sdk-cplace-single-reference-control',
  templateUrl: './cplace-single-reference-control.component.html',
  styleUrls: ['./styles/cplace-single-reference-control.component.scss'],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceSingleReferenceControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CplaceSingleReferenceControlComponent extends CplaceControlWithEditModeComponent<ReferenceValue> {
  /**
   * Component name used for control registry.
   */
  public static readonly componentName = 'CplaceSingleReferenceControlComponent';

  /**
   * Array of autocomplete hits fetched from cplace.
   */
  public autocompleteHits: AutocompleteHit[] = [];

  /**
   * Total hit count from autocomplete search.
   */
  public autocompleteTotalCountHitCount = 0;

  /**
   * Array of filtered autocomplete hits.
   */
  public filteredAutocompleteHits: AutocompleteHit[] = [];

  /**
   * Whether the autocomplete menu should be visible.
   */
  public autocompleteVisible = false;

  /**
   * Text displaying then number of items displayed over the total hit count.
   */
  public autocompleteResultsText = '';

  /**
   * Whether the empty value view should be shown.
   */
  public $showEmptyValue: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !value && !editMode;
    })
  );

  /**
   * Reference to the PrimeNG autocomplete component.
   * @private
   */
  @ViewChild('autocomplete', { read: AutoComplete, static: false })
  private autocompleteComponent!: AutoComplete;

  /**
   * Reference to the input element that contains current {@link CplaceSingleReferenceControlComponent#value}.
   * @private
   */
  @ViewChild('controlInput', { read: ElementRef, static: false })
  private controlInput!: ElementRef<HTMLInputElement>;

  /**
   * Used for builtin control autocomplete search configuration. See {@link AutocompleteConfig}.
   * @private
   */
  private oppositeEntityKind?: string;

  /**
   * Used for builtin control autocomplete search configuration. See {@link AutocompleteConfig}.
   * @private
   */
  private oppositeSpaceId?: string;

  /**
   * Used for builtin control autocomplete search configuration. See {@link AutocompleteConfig}.
   * @private
   */
  private additionalFilter?: string;

  /**
   * Used for builtin control autocomplete search configuration. See {@link AutocompleteConfig}.
   * @private
   */
  private sort?: string;

  /**
   * Used for builtin control autocomplete search configuration. See {@link AutocompleteConfig}.
   * @private
   */
  private mayCreateChild?: boolean;

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService,
    private _translationService: TranslationService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
  }

  /**
   * Maps an {@link AutocompleteHit} to a {@link ReferenceValue}.
   *
   * @param autocompleteHit An {@link AutocompleteHit}.
   * @private
   */
  private static mapToReferenceValue(autocompleteHit: AutocompleteHit): ReferenceValue {
    return {
      label: autocompleteHit.label,
      icon: autocompleteHit.icon ?? '',
      uid: autocompleteHit.uid,
      url: autocompleteHit.url ?? '',
    };
  }

  /**
   * Shows the autocomplete menu.
   */
  public showAutocomplete(): void {
    if (this.readonly || this.disabled) {
      return;
    }

    this.autocompleteVisible = true;
    this.changeDetectorRef.detectChanges();
    this.focusAndClearAutocompleteInput();
  }

  /**
   * Filters the autocomplete hits by requesting autocomplete search results from cplace based on the query and
   * autocomplete configuration.
   *
   * @param event An {@link AutoCompleteEventObject}.
   */
  public filterAutocompleteHits(event: AutoCompleteEventObject): void {
    const query: string = event.query;
    const request: BuiltinControlAutocompleteRequest = {
      query: query || '',
      oppositeEntityKind: this.oppositeEntityKind,
      oppositeSpaceId: this.oppositeSpaceId,
      additionalFilter: this.additionalFilter,
      sort: this.sort,
      mayCreateChild: this.mayCreateChild,
    };
    this.cplaceControlService
      .searchBuiltinControlAutocompleteGet(request, this.identity?.featureName ?? '')
      .pipe(untilDestroyed(this))
      .subscribe((result: AutocompleteResponse) => {
        this.filteredAutocompleteHits = result.hits;
        this.updateAutocompleteResultsText(this.filteredAutocompleteHits.length, result.totalHitCount);
        this.changeDetectorRef.detectChanges();
        if (!query && this.autocompleteHits !== []) {
          this.autocompleteHits = result.hits;
          this.autocompleteTotalCountHitCount = result.totalHitCount;
        }
      });
  }

  /**
   * Handles the autocomplete key up event. When the query is empty, the autocomplete search filter is reset and the
   * autocomplete menu is kept open.
   */
  public handleAutocompleteInputKeyUp(): void {
    if (this.autocompleteComponent.el.nativeElement.querySelector('input').value === '') {
      this.resetAutocompleteSearchFilter();
      this.autocompleteComponent.overlayVisible = true;
    }
  }

  /**
   * When pressing enter while focused on the {@link controlInput}, the autocomplete is opened.
   *
   * @param event A {@link KeyboardEvent}.
   */
  public handleControlInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.showAutocomplete();
    }
  }

  /**
   * Handles the selection of an autocomplete hit. The autocomplete is hidden after selection takes place.
   *
   * @param selectedAutocompleteHit The selected {@link AutocompleteHit}.
   */
  public handleSelectAutocompleteHit(selectedAutocompleteHit: AutocompleteHit) {
    this.value = CplaceSingleReferenceControlComponent.mapToReferenceValue(selectedAutocompleteHit);
    this.autocompleteVisible = false;
    this.changeDetectorRef.detectChanges();
    this.controlInput.nativeElement.focus();
  }

  /**
   * Handles the onRemove event from the chip. Clears the value from the control.
   */
  public handleOnRemove(): void {
    this.onClear();
  }

  /**
   * Due to how PrimeNG handles event ordering, when blurring the autocomplete, the autocomplete has to be hidden in the
   * next Angular lifecycle.
   */
  public handleBlurAutocompleteMenu(): void {
    setTimeout(() => {
      this.autocompleteVisible = false;
    });
  }

  /**
   * Due to problems with PrimeNG, the autocomplete menu has to be triggered this way.
   */
  public triggerAutocompleteMenu(): void {
    setTimeout(() => {
      this.autocompleteComponent.handleDropdownClick(null);
    });
  }

  /**
   * Opens the advanced search.
   */
  public openAdvancedSearch(): void {
    //TODO Implement later when possible
    this.onCancel();
  }

  /**
   * Opens the create new dialog.
   */
  public createNew(): void {
    //TODO Implement later when possible
    this.onCancel();
  }

  protected initializeControlParameters(controlParameters: CplaceSingleReferenceControlParameters): void {
    this.value = controlParameters.value;
  }

  protected getControlChange(identity: ControlIdentity, value: ReferenceValue): CplaceSingleReferenceControlChange {
    return { featureName: identity.featureName, componentName: CplaceSingleReferenceControlComponent.componentName, value: value?.uid };
  }

  protected getComponentClassName(): string {
    return CplaceSingleReferenceControlComponent.componentName;
  }

  protected onCancelHook() {
    this.autocompleteVisible = false;
    this.controlInput.nativeElement.blur();
  }

  protected afterSubmitHook(): void {
    super.afterSubmitHook();
    this.autocompleteVisible = false;
    this.controlInput.nativeElement.blur();
  }

  protected onClear(): void {
    this.value = undefined;
    this.showAutocomplete();
  }

  /**
   * Focuses and clears the autocomplete input.
   * @private
   */
  private focusAndClearAutocompleteInput() {
    const input: HTMLInputElement = this.autocompleteComponent.el.nativeElement.querySelector('input');
    input.value = '';
    input.focus();
  }

  /**
   * Updates the {@link autocompleteResultsText} based on the number of hits displayed and the total number of hits.
   *
   * @param hits Number of hits displayed.
   * @param total Total number of hits in the search.
   * @private
   */
  private updateAutocompleteResultsText(hits: number, total: number): void {
    this.autocompleteResultsText = this._translationService.getInstantTranslation(
      'cf-frontend-sdk.cplace-single-reference-control.autocompleteResultsText',
      {
        hits: hits,
        total: total,
      }
    );
  }

  /**
   * Resets the autocomplete search filter.
   * @private
   */
  private resetAutocompleteSearchFilter(): void {
    this.filteredAutocompleteHits = this.autocompleteHits;
    this.updateAutocompleteResultsText(this.autocompleteHits.length, this.autocompleteTotalCountHitCount);
  }
}
